import { createModal } from '@gluestack-ui/core/modal/creator'
import { withStyleContext } from '@gluestack-ui/utils/nativewind-utils'
import { Button } from 'aos-ui-universal'
import { cssInterop } from 'nativewind'
import React, { type ReactNode } from 'react'
import { Pressable, ScrollView, View } from 'react-native'

import { Icon } from '../../icons/Icon'
import { Close } from '../../icons/navmobile'
import { twMerge } from '../../tailwind/merge'
import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { modalContract } from './modal.contract'

const UIModal = createModal({
    Root: withStyleContext(View, 'MODAL'),
    Backdrop: Pressable,
    Content: View,
    Body: ScrollView,
    CloseButton: Pressable,
    Footer: View,
    Header: View,
    AnimatePresence: undefined,
})

cssInterop(UIModal, { className: 'style' })
cssInterop(UIModal.Backdrop, { className: 'style' })
cssInterop(UIModal.Content, { className: 'style' })

export const MODAL_VARIANTS = modalContract.props.variant.values

export type ModalVariant = (typeof MODAL_VARIANTS)[number]

const [TITLE_STYLE, BODY_STYLE] = modalContract.textStyleIds
const TITLE_VARIANT = variantForStyleId(TITLE_STYLE)
const BODY_VARIANT = variantForStyleId(BODY_STYLE)

/**
 * The two Figma variants are not the same tree with different tokens: in
 * `destructive` the title moves out of the body and into the header, and the
 * vertical padding grows. So the layout is written per variant rather than
 * resolved from a class map the way a primitive's is.
 */
const LAYOUT = {
    default: {
        body: 'gap-2xl p-3xl bg-background-surface items-center',
        headerStack: 'items-center',
        titleInHeader: false,
    },
    destructive: {
        body: 'gap-2xl p-3xl bg-background-surface items-center',
        headerStack: 'items-center gap-m',
        titleInHeader: true,
    },
} as const

export type ModalProps = {
    isOpen: boolean
    onClose: () => void
    variant?: ModalVariant
    title: string
    children: ReactNode
    /**
     * Figma paints a 100x100 illustration with a hard-coded white and binds no
     * token to it (see `modalContract.unbound`), so aos-ds does not ship one.
     */
    illustration?: ReactNode
    confirmLabel: string
    onConfirm: () => void
    /** Figma's `showSecondaryButton`. */
    cancelLabel?: string
    onCancel?: () => void
    /**
     * The close control's accessible name. Required, not defaulted: a dialog
     * whose only exit is unlabelled is the same defect as one with no exit, and
     * a hardcoded English string would be a translation nobody can reach.
     */
    closeLabel: string
    className?: string
    testID?: string
}

export const Modal = ({
    isOpen,
    onClose,
    variant = 'default',
    title,
    children,
    illustration,
    confirmLabel,
    onConfirm,
    cancelLabel,
    onCancel,
    closeLabel,
    className,
    testID,
}: ModalProps) => {
    const layout = LAYOUT[variant]
    const heading = (
        <Typography variant={TITLE_VARIANT} tone='default' testID='modal-title'>
            {title}
        </Typography>
    )

    return (
        <UIModal isOpen={isOpen} onClose={onClose} testID={testID ?? 'modal'}>
            <UIModal.Backdrop />
            <UIModal.Content
                className={twMerge(
                    'overflow-hidden rounded-message-mobile w-[26.125rem]',
                    className,
                )}
            >
                <UIModal.Header className='relative bg-background-sub-surface'>
                    <View className={twMerge('flex-1', layout.headerStack)}>
                        {illustration}
                        {layout.titleInHeader ? heading : null}
                    </View>
                    <UIModal.CloseButton
                        onPress={onClose}
                        className='absolute right-0 top-0 p-m'
                        accessibilityLabel={closeLabel}
                        accessibilityRole='button'
                        testID='modal-close'
                    >
                        <Icon glyph={Close} tone='default' />
                    </UIModal.CloseButton>
                </UIModal.Header>

                <UIModal.Body className={layout.body}>
                    {layout.titleInHeader ? null : heading}
                    <Typography variant={BODY_VARIANT} tone='default'>
                        {children}
                    </Typography>

                    <View className='flex-row items-center gap-2xl'>
                        {cancelLabel ? (
                            <Button
                                size='small'
                                type='secondary'
                                onPress={onCancel ?? onClose}
                                testID='modal-cancel'
                            >
                                {cancelLabel}
                            </Button>
                        ) : null}
                        <Button
                            size='small'
                            type={variant === 'destructive' ? 'destructive' : 'primary'}
                            onPress={onConfirm}
                            testID='modal-confirm'
                        >
                            {confirmLabel}
                        </Button>
                    </View>
                </UIModal.Body>
            </UIModal.Content>
        </UIModal>
    )
}

Modal.displayName = 'Modal'
